/**
 * Application routes configuration
 * Centralized place to define all route paths for reusability
 */

import { useMatch } from 'react-router-dom';

// Parent route path constants
export const PARENT_ROUTES = {
  HOME: '/',
  ORG: '/organizations/:orgId',
  WORKSPACE: '/organizations/:orgId/workspaces/:workspaceId',
} as const;

// Onboarding step constants
export const ONBOARDING_STEPS = {
  ORGANIZATION: 'organization',
  WORKSPACE: 'workspace',
  USER_INTENT: 'user-intent',
} as const;

export type TOnboardingStep =
  (typeof ONBOARDING_STEPS)[keyof typeof ONBOARDING_STEPS];

// Route parameter types
export interface IRouteParams {
  [key: string]: string | number;
}

// Route pattern type - for defining route patterns with placeholders
export interface IRoutePattern {
  pattern: string;
  build: (params: IRouteParams) => string;
}

// Route definition type - can be a string, function, or pattern object
export type TRouteDefinition =
  | string
  | ((params: IRouteParams) => string)
  | IRoutePattern;

// Routes configuration
export const ROUTES = {
  // Main routes (no params)
  HOME: '/',
  NOT_FOUND: '/not-found',
  LOGIN: '/login',
  ONBOARDING: {
    pattern: '/onboarding/:onboardingStep?',
    build: (params: IRouteParams) =>
      `/onboarding/${params.onboardingStep ?? ''}`,
  },
  INVITATION: {
    pattern: '/onboarding/invite/:invitationId',
    build: (params: IRouteParams) =>
      `/onboarding/invite/${params.invitationId}`,
  },
  API_KEYS: '/api-keys',
  PREFERENCES: '/preferences',
  TUTORIALS: '/tutorials',
  USER_SETTINGS: '/settings/user',
  VNC_VIEWER: '/vnc-viewer',

  // Organization routes (only orgId)
  ORGANIZATION_PERFORMANCE: {
    pattern: '/organizations/:orgId/performance',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/performance`,
  },
  REMOTE_CONNECTIONS: {
    pattern: '/organizations/:orgId/remote-connections',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/remote-connections`,
  },
  REMOTE_CONNECTIONS_CREATE: {
    pattern: '/organizations/:orgId/remote-connections/create',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/remote-connections/create`,
  },
  REMOTE_CONNECTIONS_DETAIL: {
    pattern: '/organizations/:orgId/remote-connections/:remoteConnectionId',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/remote-connections/${params.remoteConnectionId}`,
  },
  REMOTE_CONNECTIONS_EDIT: {
    pattern:
      '/organizations/:orgId/remote-connections/:remoteConnectionId/edit',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/remote-connections/${params.remoteConnectionId}/edit`,
  },

  // Workspace routes (orgId + workspaceId)
  DRAFTS: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/drafts',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/drafts`,
  },
  DRAFT_EDITOR: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/drafts/:draftId/edit',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/drafts/${params.draftId}/edit`,
  },
  DRAFT_PRE_RUN: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/drafts/:draftId/run',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/drafts/${params.draftId}/run`,
  },
  DRAFT_RUN: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/drafts/:draftId/runs/:runId',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/drafts/${params.draftId}/runs/${params.runId}`,
  },
  AUTOMATIONS: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/automations',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/automations`,
  },
  LEARNINGS: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/learnings',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/learnings`,
  },
  GUIDANCE_CENTER: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/guidance-center',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/guidance-center`,
  },
  TROUBLESHOOTING_GUIDE: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/troubleshooting-guides',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/troubleshooting-guides`,
  },
  ACTION_ITEM_DETAIL: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/guidance-center/:actionItemId',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/guidance-center/${params.actionItemId}`,
  },
  ACTION_ITEM_REVIEW: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/guidance-center/:actionItemId/review',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/guidance-center/${params.actionItemId}/review`,
  },
  AUTOMATION_AOP: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/automations/:automationId/aop',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/automations/${params.automationId}/aop`,
  },
  AUTOMATION_DETAILS: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/automations/:automationId/details',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/automations/${params.automationId}/details`,
  },
  AUTOMATION_PRE_RUN: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/automations/:automationId/run',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/automations/${params.automationId}/run`,
  },
  AUTOMATION_RUN: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/automations/:automationId/runs/:runId',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/automations/${params.automationId}/runs/${params.runId}`,
  },

  // Integrations routes (orgId + workspaceId)
  INTEGRATIONS: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/integrations',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/integrations`,
  },
  INTEGRATIONS_CONNECTIONS: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/integrations/connections',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/integrations/connections`,
  },
  INTEGRATIONS_INTEGRATION_DETAILS: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/integrations/:integrationName/:integrationVersion',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/integrations/${params.integrationName}/${params.integrationVersion}`,
  },

  // Workspace Settings (orgId + workspaceId)
  SETTINGS: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/settings',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/settings`,
  },
  WORKSPACE_SETTINGS: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/settings',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/settings`,
  },
  WORKSPACE_SETTINGS_OVERVIEW: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/settings/overview',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/settings/overview`,
  },
  WORKSPACE_SETTINGS_PEOPLE: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/settings/users',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/settings/users`,
  },
  WORKSPACE_SETTINGS_PREFERENCES: {
    pattern:
      '/organizations/:orgId/workspaces/:workspaceId/settings/preferences',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/settings/preferences`,
  },
  WORKSPACE_ACCESS_DENIED: {
    pattern: '/organizations/:orgId/workspaces/:workspaceId/access-denied',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/workspaces/${params.workspaceId}/access-denied`,
  },

  // Org Settings (only orgId)
  ORG_SETTINGS: {
    pattern: '/organizations/:orgId/settings',
    build: (params: IRouteParams) => `/organizations/${params.orgId}/settings`,
  },
  ORG_SETTINGS_OVERVIEW: {
    pattern: '/organizations/:orgId/settings/overview',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/settings/overview`,
  },
  ORG_SETTINGS_WORKSPACES: {
    pattern: '/organizations/:orgId/settings/workspaces',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/settings/workspaces`,
  },
  ORG_SETTINGS_PEOPLE: {
    pattern: '/organizations/:orgId/settings/users',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/settings/users`,
  },
  ORG_SETTINGS_PREFERENCES: {
    pattern: '/organizations/:orgId/settings/preferences',
    build: (params: IRouteParams) =>
      `/organizations/${params.orgId}/settings/preferences`,
  },
} as const satisfies Record<string, TRouteDefinition>;

/**
 * Type for route keys
 */
export type TRouteKey = keyof typeof ROUTES;

/**
 * Helper function to get route path by key
 */
export const getRoutePath = (key: TRouteKey, params?: IRouteParams) => {
  const route = ROUTES[key];
  if (typeof route === 'function') {
    if (!params) {
      throw new Error(
        `Route ${key} requires parameters but none were provided`,
      );
    }
    return (route as (params: IRouteParams) => string)(params);
  }

  if (typeof route === 'object' && route !== null && 'pattern' in route) {
    if (!params) {
      return route.pattern;
    }
    return route.build(params);
  }

  return route;
};

/**
 * Helper function to check if a path matches a route
 */
export const isRoute = (
  path: string,
  route: TRouteKey,
  params?: IRouteParams,
): boolean => {
  const routePath = getRoutePath(route, params);
  return path === routePath;
};

/**
 * Helper function to get route pattern (useful for React Router)
 */
export const getRoutePattern = (key: TRouteKey): string => {
  const route = ROUTES[key];

  if (typeof route === 'string') {
    return route;
  }

  if (typeof route === 'object' && 'pattern' in route) {
    return route.pattern;
  }

  // For function-based routes, we can't determine the pattern
  // You might want to add a pattern property to function-based routes as well
  throw new Error(`Route ${key} does not have a defined pattern`);
};

export const useIsOnboardingOrInvitationPage = () => {
  const isOnboardingPage = Boolean(useMatch(getRoutePattern('ONBOARDING')));

  const isAcceptInvitationPage = Boolean(
    useMatch(getRoutePattern('INVITATION')),
  );

  return isOnboardingPage || isAcceptInvitationPage;
};

export const constructInvitationUrl = ({
  invitationId,
  organizationId,
}: {
  invitationId: string;
  organizationId: string;
}) => {
  return `${window.location.origin}${getRoutePath('INVITATION', { invitationId })}?organizationId=${organizationId}`;
};
